import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { LoggerService } from './logger.service';

export interface VoiceRecordingResult {
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
}

/**
 * Browser audio capture for voice input.
 *
 * Wraps getUserMedia + MediaRecorder with cross-browser mime negotiation:
 * Chrome/Edge/Firefox record audio/webm (opus); iOS Safari records audio/mp4
 * (AAC). The negotiated mime type is reported so the transcription backend
 * receives the real container format.
 */
@Injectable({
  providedIn: 'root'
})
export class VoiceCaptureService {
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private mimeType = '';
  private startedAt = 0;
  private elapsedTimer?: number;

  private elapsedSubject = new Subject<number>();

  /** Emits elapsed whole seconds while a recording is active. */
  get elapsed$(): Observable<number> {
    return this.elapsedSubject.asObservable();
  }

  constructor(private logger: LoggerService) {}

  isSupported(): boolean {
    return typeof navigator !== 'undefined'
      && !!navigator.mediaDevices?.getUserMedia
      && typeof MediaRecorder !== 'undefined';
  }

  get isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  /**
   * Request the microphone and begin recording.
   * Must be called from a user gesture (iOS Safari requirement).
   */
  async start(): Promise<void> {
    if (this.isRecording) {
      this.logger.warn('VoiceCaptureService', 'start', 'Recording already in progress');
      return;
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.mimeType = this.negotiateMimeType();
    this.chunks = [];

    this.mediaRecorder = this.mimeType
      ? new MediaRecorder(this.mediaStream, { mimeType: this.mimeType })
      : new MediaRecorder(this.mediaStream);
    this.mimeType = this.mediaRecorder.mimeType || this.mimeType || 'audio/webm';

    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };

    this.mediaRecorder.start(1000);
    this.startedAt = Date.now();

    this.elapsedTimer = window.setInterval(() => {
      this.elapsedSubject.next(Math.floor((Date.now() - this.startedAt) / 1000));
    }, 250);

    this.logger.info('VoiceCaptureService', 'start', 'Recording started', { mimeType: this.mimeType });
  }

  /** Stop recording and return the captured audio. */
  stop(): Promise<VoiceRecordingResult> {
    return new Promise((resolve, reject) => {
      const recorder = this.mediaRecorder;
      if (!recorder || recorder.state === 'inactive') {
        this.cleanup();
        reject(new Error('No active recording'));
        return;
      }

      const durationSeconds = Math.max(1, Math.round((Date.now() - this.startedAt) / 1000));

      recorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.mimeType });
        const mimeType = this.mimeType;
        this.cleanup();
        this.logger.info('VoiceCaptureService', 'stop', 'Recording stopped', {
          mimeType,
          durationSeconds,
          sizeBytes: blob.size
        });
        resolve({ blob, mimeType, durationSeconds });
      };

      recorder.stop();
    });
  }

  /** Discard the current recording without producing a result. */
  cancel(): void {
    const recorder = this.mediaRecorder;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }
    this.cleanup();
    this.logger.info('VoiceCaptureService', 'cancel', 'Recording cancelled');
  }

  private negotiateMimeType(): string {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
      return '';
    }

    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4'
    ];

    return candidates.find(type => MediaRecorder.isTypeSupported(type)) ?? '';
  }

  private cleanup(): void {
    if (this.elapsedTimer !== undefined) {
      window.clearInterval(this.elapsedTimer);
      this.elapsedTimer = undefined;
    }

    // Release the mic — leaked streams break subsequent captures on iOS.
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = null;
    this.mediaRecorder = null;
    this.chunks = [];
  }
}
